import { useSyncExternalStore } from "react";
import { localTranslator, type LocalTranslator } from "./mailTranslation";

type Pair = "en-zh" | "zh-en";
type State = "checking" | "ready" | "partial" | "setup" | "unavailable" | "error";
let state: State = "checking";
let started = false;
let generation = 0;
let controller = new AbortController();
let queue = Promise.resolve();
let activation: Promise<void> | null = null;
const listeners = new Set<() => void>();
const translators = new Map<Pair, LocalTranslator>();
const setupPairs = new Set<Pair>();
const translations = new Map<string, Promise<string | null>>();
const PAIRS: Record<Pair, { sourceLanguage: string; targetLanguage: string }> = {
  "en-zh": { sourceLanguage: "en", targetLanguage: "zh" },
  "zh-en": { sourceLanguage: "zh", targetLanguage: "en" },
};

function announce(next: State) { state = next; listeners.forEach(listener => listener()); }
export function subscribeTitleTranslation(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function useTitleTranslationState() { return useSyncExternalStore(subscribeTitleTranslation, () => state, () => "unavailable" as State); }

export function titlePair(title: string): Pair | null {
  if (title === "旧记录 · 未保存标题" || title === "（无主题）") return null;
  const value = title.replace(/^(?:(?:re|fw|fwd)\s*:\s*)+/i, "").trim();
  if (!value || /[\u3040-\u30ff\uac00-\ud7af]/u.test(value)) return null;
  const han = (value.match(/\p{Script=Han}/gu) || []).length;
  const latin = (value.match(/[A-Za-z]/g) || []).length;
  if (han >= 2) return "zh-en";
  if (!han && value.length >= 6 && latin >= 8 && /[A-Za-z]{2,}\s+[A-Za-z]{2,}/.test(value)) return "en-zh";
  return null;
}

function protectedParts(value: string) {
  return value.match(/(?:https?:\/\/\S+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|\[[A-Za-z][A-Za-z0-9_-]*\d[A-Za-z0-9_-]*\]|\b\d[\d -]{3,}\d\b)/gi) || [];
}
function escapeRegex(value: string) { return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

export function refineTitleTranslation(original: string, translated: string, pair: Pair): string | null {
  let value = translated.trim();
  // The local translator sometimes changes the case of order/reference IDs.
  for (const part of protectedParts(original)) value = value.replace(new RegExp(escapeRegex(part), "gi"), part);
  if (pair === "en-zh") {
    const lead = /^\s*(?:(?:re|fw|fwd)\s*:\s*)*(just dropped|priority access)\s*[:：]/i.exec(original)?.[1]?.toLowerCase();
    const colon = value.search(/[:：]/);
    if (lead && colon >= 0 && colon < 24) value = (lead === "just dropped" ? "新品上架：" : "抢先选购：") + value.slice(colon + 1).trimStart();
    if (/\border is on its way\b/i.test(original)) {
      value = value.replace(/订单(?:正在进行中|在路上|正在路上|在途中)/g, "订单已发货，正在配送");
    }
    if (/\btakes the lead\b/i.test(original)) value = value.replace(/带头|领先于?/g, "成为主角");
    if (/\bvelvety soft set\b/i.test(original)) value = value.replace(/天鹅绒软套/g, "柔软天鹅绒套装");
    if (/\bdressy boots\b/i.test(original)) value = value.replace(/考究靴/g, "精致靴款");
    if (/\bnights out\s*&\s*events\b/i.test(original)) value = value.replace(/夜晚和活动/g, "晚间出行和聚会");
    if (/\bweekly deals?\b.*\bfave(?:ou?rite)? restaurants?\b.*\binside\b/i.test(original)) {
      value = "你喜爱的餐厅本周有优惠，点开查看";
    }
  }
  return usableTitleTranslation(original, value, pair) ? value : null;
}

export function bilingualTitleLines(original: string, translated: string | null, pair: Pair | null) {
  if (pair === "zh-en" && translated) return { primary: translated, secondary: original, primaryLang: "en", secondaryLang: "zh-CN" };
  return { primary: original, secondary: pair === "en-zh" ? translated : null, primaryLang: pair === "en-zh" ? "en" : pair === "zh-en" ? "zh-CN" : undefined, secondaryLang: "zh-CN" };
}
export function usableTitleTranslation(original: string, translated: string, pair: Pair) {
  const candidate = translated.trim();
  if (!candidate || candidate.toLocaleLowerCase() === original.trim().toLocaleLowerCase() || candidate.length > Math.max(300, original.length * 4)) return false;
  if (pair === "en-zh" && !/\p{Script=Han}/u.test(candidate)) return false;
  if (pair === "zh-en" && !/[A-Za-z]{2}/.test(candidate)) return false;
  return protectedParts(original).every(part => candidate.includes(part));
}

export function desktopChromeBrowser() {
  return typeof navigator !== "undefined" && /Chrome\/\d+/.test(navigator.userAgent)
    && !/(?:Android|Mobile|Edg\/|OPR\/)/.test(navigator.userAgent);
}
export function desktopChromeTranslator() { return desktopChromeBrowser() && !!localTranslator(); }

export async function startTitleTranslation() {
  if (started) return;
  started = true;
  const currentGeneration = generation;
  if (!desktopChromeTranslator()) { announce("unavailable"); return; }
  const factory = localTranslator()!;
  if (!factory.availability) { announce("setup"); return; }
  try {
    const availability = await Promise.all((Object.keys(PAIRS) as Pair[]).map(pair => factory.availability!(PAIRS[pair])));
    if (currentGeneration !== generation) return;
    if (availability.every(value => value === "unavailable")) { announce("unavailable"); return; }
    // An already-downloaded pack can start without a click. Downloadable packs require activation.
    const available = (Object.keys(PAIRS) as Pair[]).filter((_, index) => availability[index] === "available");
    if (activation) await activation;
    if (currentGeneration !== generation) return;
    (Object.keys(PAIRS) as Pair[]).forEach((pair, index) => { if (!translators.has(pair) && (availability[index] === "downloadable" || availability[index] === "downloading")) setupPairs.add(pair); });
    const toCreate = available.filter(pair => !translators.has(pair));
    const created = await Promise.allSettled(toCreate.map(pair => factory.create(PAIRS[pair])));
    if (currentGeneration !== generation) { created.forEach(result => { if (result.status === "fulfilled") result.value.destroy(); }); return; }
    created.forEach((result, index) => { if (result.status === "fulfilled") translators.set(toCreate[index], result.value); });
    announce(translators.size ? setupPairs.size ? "partial" : "ready" : "setup");
  } catch { if (currentGeneration === generation) announce("setup"); }
}

export async function activateTitleTranslation() {
  if (activation) return activation;
  const factory = localTranslator();
  if (!desktopChromeTranslator() || !factory) { announce("unavailable"); return; }
  const currentGeneration = generation;
  // Invoke create synchronously in the click handler, before the first await.
  const missing = (Object.keys(PAIRS) as Pair[]).filter(pair => !translators.has(pair) && (!setupPairs.size || setupPairs.has(pair)));
  const attempts = missing.map(pair => { try { return factory.create(PAIRS[pair]); } catch (error) { return Promise.reject(error); } });
  announce("checking");
  const pending = Promise.allSettled(attempts).then(created => {
    if (currentGeneration !== generation) { created.forEach(result => { if (result.status === "fulfilled") result.value.destroy(); }); return; }
    created.forEach((result, index) => { if (result.status === "fulfilled") { translators.set(missing[index], result.value); setupPairs.delete(missing[index]); } });
    translations.clear();
    announce(translators.size ? setupPairs.size ? "partial" : "ready" : "error");
  });
  const tracked = pending.finally(() => { if (activation === tracked) activation = null; });
  activation = tracked;
  return activation;
}

export function translateTitle(title: string): Promise<string | null> {
  const pair = titlePair(title);
  if (!pair || !translators.has(pair)) return Promise.resolve(null);
  const key = pair + ":" + title;
  const cached = translations.get(key);
  if (cached) return cached;
  const currentGeneration = generation;
  const signal = controller.signal;
  const pending = queue.then(async () => {
    if (signal.aborted || currentGeneration !== generation) return null;
    try {
      const value = await translators.get(pair)!.translate(title.replace(/\bfave\b/gi, "favorite"), { signal });
      return !signal.aborted && currentGeneration === generation ? refineTitleTranslation(title, value, pair) : null;
    } catch { return null; }
  });
  queue = pending.then(() => undefined);
  translations.set(key, pending);
  if (translations.size > 500) translations.delete(translations.keys().next().value!);
  return pending;
}

export function clearTitleTranslation() {
  generation++;
  controller.abort(); controller = new AbortController();
  translators.forEach(translator => translator.destroy()); translators.clear();
  setupPairs.clear(); translations.clear(); queue = Promise.resolve(); activation = null; started = false;
  announce("checking");
}
